import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageShell from "@/components/PageShell";
import api from "@/lib/api";
import { Layers, Trash2, ArrowRight, ExternalLink, ChevronDown, Sparkles, Compass } from "lucide-react";
import { toast } from "sonner";

export default function MyPaths() {
  const navigate = useNavigate();
  const [paths, setPaths] = useState([]);
  const [open, setOpen] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = () => api.get("/oracle/paths").then((r) => { setPaths(r.data); setLoaded(true); }).catch(() => setLoaded(true));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try { await api.delete(`/oracle/paths/${id}`); setPaths((p) => p.filter((x) => x.id !== id)); toast.success("Path removed"); }
    catch { toast.error("Could not remove"); }
  };
  const goStep = (s) => {
    if (s.url) { window.open(s.url, "_blank", "noopener"); return; }
    if (s.to) navigate(s.to);
  };

  return (
    <PageShell>
      <div className="max-w-4xl mx-auto px-6 py-14">
        <span className="text-xs uppercase tracking-widest font-mono text-primary">From the Oracle</span>
        <div className="flex items-center gap-3 mt-2 mb-3">
          <Compass className="w-7 h-7 text-primary" />
          <h1 className="font-serif text-5xl">My Paths</h1>
        </div>
        <p className="text-muted-foreground max-w-2xl mb-10">
          Guided paths Anubis built from your questions. Revisit any layer and jump straight to the courses, terms,
          cases and statutes it references.
        </p>

        {loaded && paths.length === 0 && (
          <div className="border border-border bg-card p-10 text-center" data-testid="mypaths-empty">
            <Layers className="w-8 h-8 text-primary mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">No saved paths yet. Ask the Anubis Oracle a question and tap
              <span className="text-foreground"> "Save this path"</span> to keep it here.</p>
          </div>
        )}

        <div className="space-y-4">
          {paths.map((p) => {
            const c = p.course || {};
            return (
              <div key={p.id} className="border border-border bg-card" data-testid={`mypath-${p.id}`}>
                <div className="flex items-start gap-3 p-5 border-b border-border">
                  <Layers className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <h2 className="font-serif text-2xl leading-tight">{c.title || p.title}</h2>
                    {c.summary && <p className="text-sm text-muted-foreground mt-1">{c.summary}</p>}
                    <p className="text-[11px] font-mono text-muted-foreground mt-2 truncate">“{p.question}”</p>
                  </div>
                  <button onClick={() => remove(p.id)} data-testid={`mypath-delete-${p.id}`} className="text-muted-foreground hover:text-destructive shrink-0"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="p-5 space-y-2">
                  {(c.layers || []).map((layer, li) => {
                    const key = `${p.id}-${li}`;
                    return (
                      <div key={li} className="border border-border rounded-sm overflow-hidden">
                        <button onClick={() => setOpen(open === key ? null : key)}
                          className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-muted">
                          <span className="w-5 h-5 rounded-full bg-primary/15 text-primary text-[10px] font-mono flex items-center justify-center shrink-0">{li + 1}</span>
                          <span className="text-sm font-medium flex-1 min-w-0">{layer.title}</span>
                          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${open === key ? "rotate-180" : ""}`} />
                        </button>
                        {open === key && (
                          <div className="px-3 pb-3 pt-1 space-y-2">
                            {layer.summary && <p className="text-xs text-muted-foreground">{layer.summary}</p>}
                            {(layer.steps || []).map((s, si) => (
                              <div key={si} className="flex items-start gap-2 text-sm bg-muted/50 rounded-sm px-2.5 py-2">
                                <Sparkles className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <div className="font-medium">{s.label}</div>
                                  {s.detail && <div className="text-xs text-muted-foreground">{s.detail}</div>}
                                  {(s.to || s.url) && (
                                    <button onClick={() => goStep(s)} className="text-[11px] font-mono uppercase tracking-widest text-primary hover:underline inline-flex items-center gap-1 mt-1">
                                      Open {s.url ? <ExternalLink className="w-3 h-3" /> : <ArrowRight className="w-3 h-3" />}
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </PageShell>
  );
}
